import { hasAnyRole, ROLES } from '@/lib/constants/roles';
import { ENTITY_TYPES } from '@/lib/constants/entity-types';
import { ISSUE_REPORT_OFFER_STATE } from '@/lib/constants/issue-report';
import type { SessionUser } from '@/lib/types/auth.types';
import type { IssueReportOffer } from '@/lib/types/billing';
import type { DataRoomEntitlement } from './api/data-room.api';

/** The slice of a React Query result the resolver reads, so it stays pure and easy to test. */
export interface JourneyQuery<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
}

/** The slice of a React Query result `toJourneyQuery` reads. */
interface QueryResultSlice<T> extends JourneyQuery<T> {
  isFetching: boolean;
}

/**
 * A query result as the resolver wants it: still loading while a request is in
 * flight and nothing has arrived. React Query's own `isLoading` is false during
 * a retry after a failure (the query is in `error`, not `pending`), which would
 * keep the failed screen up with no sign of progress; a refetch with data
 * already in hand is not a wait.
 */
export function toJourneyQuery<T>(query: QueryResultSlice<T>): JourneyQuery<T> {
  return {
    data: query.data,
    isLoading: query.isLoading || (query.isFetching && query.data === undefined),
    isError: query.isError,
  };
}

export interface JourneyInput {
  offer: JourneyQuery<IssueReportOffer>;
  /** The session: `user` is null with no session, `isLoading` while it hydrates. */
  auth: { user: SessionUser | null; isLoading: boolean };
  /** The startup's data-room entitlement; only read for a Startup Admin. */
  access: JourneyQuery<DataRoomEntitlement>;
}

/** Where the Issue Report CTA sends this visitor next (design §38, Card 27). */
export type JourneyStep =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'coming_soon' }
  | { kind: 'login' }
  | { kind: 'create_startup' }
  | { kind: 'admin_only' }
  | { kind: 'data_room' }
  | { kind: 'checkout'; planId: string };

/**
 * Refusals that mean the startup holds no access to buy against. Any other
 * reason (a seat refusal, suspension, a missing company email...) means access
 * exists or is being sorted out, so the Data Room page explains it; sending
 * that admin to checkout would only meet the Backend's refusal of a second
 * purchase (A-109).
 */
const NO_ACCESS_REASONS: ReadonlySet<string> = new Set(['no_active_service', 'entitlement_expired']);

/**
 * The next step of the Issue Report journey (FR-204, FR-205). Pure: the same
 * inputs always give the same step, and every wait is explicit, so the page
 * shows a loading state rather than a wrong answer (never an Access Denied
 * while the session or the access view is still arriving).
 *
 * Order matters: the offer first (a plan that is off stops everyone, logged in
 * or not), then the session, then the startup, the role and finally access.
 * The plan id comes only from the offer, never from a constant or the plan list.
 */
export function resolveIssueReportStep({ offer, auth, access }: JourneyInput): JourneyStep {
  if (offer.isLoading) return { kind: 'loading' };
  if (!offer.data) return { kind: 'error' };
  if (offer.data.state === ISSUE_REPORT_OFFER_STATE.COMING_SOON) return { kind: 'coming_soon' };

  if (auth.isLoading) return { kind: 'loading' };
  const { user } = auth;
  if (!user) return { kind: 'login' };

  // A Super Admin has no startup and nothing to buy for one.
  if (hasAnyRole(user.roles, [ROLES.SUPER_ADMIN])) return { kind: 'admin_only' };
  if (!user.activeMembership) return { kind: 'create_startup' };

  const isStartupAdmin =
    user.activeMembership.entityType === ENTITY_TYPES.STARTUP && hasAnyRole(user.roles, [ROLES.STARTUP_ADMIN]);
  if (!isStartupAdmin) return { kind: 'admin_only' };

  if (access.isLoading) return { kind: 'loading' };
  if (!access.data) return { kind: 'error' };
  const hasAccess = access.data.entitled || !NO_ACCESS_REASONS.has(access.data.reason);
  return hasAccess ? { kind: 'data_room' } : { kind: 'checkout', planId: offer.data.planId };
}
